/**
 * AnimalDropBox Component
 *
 * A drop target box for the animal password system.
 * Students drag animals here to build their password.
 */

import { memo } from 'react';
import { Droppable } from '../DragDrop';
import type { DragItem } from '../DragDrop';
import {
  type AnimalDropBoxProps,
  type AnimalTileDragData,
  getAnimalEmoji,
  getAnimalLabel,
} from './types';
import { ANIMAL_DRAG_TYPE } from './AnimalTile';

/**
 * Size classes for different box sizes
 */
const SIZE_CLASSES = {
  sm: 'w-16 h-16 text-2xl',
  md: 'w-20 h-20 text-3xl',
  lg: 'w-24 h-24 text-4xl',
} as const;

/**
 * AnimalDropBox - Drop target for animal tiles
 * Supports both drag-and-drop AND tap-and-tap input modes
 */
export const AnimalDropBox = memo(function AnimalDropBox({
  index,
  animal,
  dropEnabled = true,
  onDrop,
  onClick,
  selectedAnimal,
  onPlaceSelected,
  size = 'md',
  className = '',
  testId,
}: AnimalDropBoxProps) {
  const sizeClasses = SIZE_CLASSES[size];
  const hasAnimal = animal !== null && animal !== undefined;
  const hasSelectedAnimal = selectedAnimal !== null && selectedAnimal !== undefined;

  // Handle drop from drag operation
  const handleDrop = (item: DragItem) => {
    if (!dropEnabled || !onDrop) return { targetId: `animal-box-${index}`, success: false };

    const data = item.data as AnimalTileDragData;
    if (data?.type === 'animal' && data.animal) {
      onDrop(index, data.animal);
      return { targetId: `animal-box-${index}`, success: true };
    }
    return { targetId: `animal-box-${index}`, success: false };
  };

  // Handle click - either remove animal or place selected animal
  const handleClick = () => {
    if (hasAnimal && onClick) {
      // Click on filled box = remove animal
      onClick(index);
    } else if (!hasAnimal && hasSelectedAnimal && onPlaceSelected) {
      // Click on empty box with selected animal = place it
      onPlaceSelected(index);
    }
  };

  // Render the box content
  const renderContent = (isOver: boolean, canDrop: boolean) => {
    // Check if this empty box can receive the selected animal
    const canReceiveSelected = !hasAnimal && hasSelectedAnimal && onPlaceSelected;

    // Determine background color
    let bgClasses = 'bg-neutral-100';
    if (isOver && canDrop) {
      bgClasses = 'bg-blue-100';
    } else if (canReceiveSelected) {
      bgClasses = 'bg-magic-50';
    } else if (hasAnimal) {
      bgClasses = 'bg-white';
    }

    // Determine border color
    let borderClasses = 'border-neutral-300 border-dashed';
    if (isOver && canDrop) {
      borderClasses = 'border-blue-400 border-solid';
    } else if (canReceiveSelected) {
      borderClasses = 'border-magic-400 border-dashed animate-pulse';
    } else if (hasAnimal) {
      borderClasses = 'border-neutral-200 border-solid';
    }

    // Determine if box is clickable
    const isClickable = (hasAnimal && onClick) || canReceiveSelected;

    return (
      <div
        className={`
          ${sizeClasses}
          flex items-center justify-center
          rounded-xl border-3
          ${bgClasses}
          ${borderClasses}
          transition-all duration-150
          ${hasAnimal && onClick ? 'cursor-pointer hover:bg-red-50 hover:border-red-300' : ''}
          ${canReceiveSelected ? 'cursor-pointer hover:bg-magic-100 hover:border-magic-500' : ''}
          ${className}
        `}
        onClick={handleClick}
        role={isClickable ? 'button' : undefined}
        aria-label={
          hasAnimal
            ? `Box ${index + 1}: ${getAnimalLabel(animal!)}. Click to remove.`
            : canReceiveSelected
              ? `Box ${index + 1}: Empty. Tap to place ${getAnimalLabel(selectedAnimal!)}.`
              : `Box ${index + 1}: Empty. Drop an animal here.`
        }
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }
            : undefined
        }
        data-testid={testId ?? `animal-box-${index}`}
        data-box-index={index}
        data-has-animal={hasAnimal}
        data-can-receive={canReceiveSelected}
      >
        {hasAnimal ? (
          <span className="drop-shadow-sm">{getAnimalEmoji(animal!)}</span>
        ) : (
          <span className="text-neutral-400 text-2xl">?</span>
        )}
      </div>
    );
  };

  // If drop is not enabled, just render content without Droppable
  if (!dropEnabled) {
    return renderContent(false, false);
  }

  return (
    <Droppable
      id={`animal-box-${index}`}
      accept={ANIMAL_DRAG_TYPE}
      canDrop={() => !hasAnimal} // Only accept drops on empty boxes
      onDrop={handleDrop}
      ariaLabel={`Animal password box ${index + 1}`}
      testId={testId ?? `animal-box-${index}-droppable`}
    >
      {({ isOver, canDrop }) => renderContent(isOver, canDrop)}
    </Droppable>
  );
});

AnimalDropBox.displayName = 'AnimalDropBox';

export default AnimalDropBox;
